import { get, globalPut, post, remove, url, tokenRef } from "api";

// A SAVED FILTER, APPLIED TO THE REPORT (owner, 2026-10-15): the issue list's
// and the shop drawings table's own saved filters, sent as their query string
// with the name the document prints. The server resolves each through the
// list's own builder.
const appendSavedFilters = (q, { issueFilter, issueFilterName, shopDrawingFilter, shopDrawingFilterName, ganttItemId, basis, openOnly, ...rest } = {}) => {
  // ESTIMATED HOURS AND OPEN ITEMS (owner, 2026-10-16): the cost projected from
  // each item's estimate, and only issues still open / drawings not approved.
  if (basis === "estimated") q.set("basis", "estimated");
  // TRADE, STATUS, TAG (owner, 2026-10-16) - on issues and drawings alike.
  if (rest.trade) q.set("trade", rest.trade);
  if (rest.issueStatus && rest.issueStatus.length) q.set("issueStatus", [].concat(rest.issueStatus).join(","));
  if (rest.drawingStatus && rest.drawingStatus.length) q.set("drawingStatus", [].concat(rest.drawingStatus).join(","));
  if (rest.tag) q.set("tag", rest.tag);
  if (rest.assignee) { q.set("assignee", rest.assignee); if (rest.assigneeName) q.set("assigneeName", rest.assigneeName); }
  // PARTNER TEAM (owner, 2026-10-22) - every item assigned to anyone on that
  // company, which is a different question from whose hours are on it. The
  // name rides along only so the document can say the filter in words.
  if (rest.partnerTeam) { q.set("partnerTeam", rest.partnerTeam); if (rest.partnerTeamName) q.set("partnerTeamName", rest.partnerTeamName); }
  if (openOnly) q.set("openOnly", "1");
  // An activity at any level; the server takes everything below it.
  if (ganttItemId) q.set("ganttItemId", ganttItemId);
  if (issueFilter) { q.set("issueFilter", issueFilter); if (issueFilterName) q.set("issueFilterName", issueFilterName); }
  if (shopDrawingFilter) { q.set("shopDrawingFilter", shopDrawingFilter); if (shopDrawingFilterName) q.set("shopDrawingFilterName", shopDrawingFilterName); }
};

// EVERY CONTRACTOR'S HOURS AND COST, across every billable system.
//
// The owning company is taken from the signed-in user on the server, never
// sent from here - there is no parameter in this call that can reach another
// tenant's numbers. `projectId` and `contractorId` only ever narrow.
export const getBillableHours = ({ projectId, contractorId, since, until, ...saved } = {}) => {
  const q = new URLSearchParams();
  if (projectId) q.set("projectId", projectId);
  if (contractorId) q.set("contractorId", contractorId);
  if (since) q.set("since", since);
  if (until) q.set("until", until);
  appendSavedFilters(q, saved);
  const qs = q.toString();
  return get(`/billable-hours${qs ? `?${qs}` : ""}`);
};

// MAY THE SIGNED-IN PERSON OPEN THE PAGE? contractorsCost is a platform key,
// so it never appears in the permission lists the SPA holds; the server is
// the only place that can answer. Resolves { ok }.
export const getBillableAccess = () => get("/billable-hours/access");

// THE SAME REPORT WITH EVERY LINE ITEM: each contractor gains `rows`, each
// row naming where its hours came from (`item`: kind, label, title, id) and,
// when it cannot be attributed, why (`unidentified`). Same gate and same
// totals as above - the server builds both from one pass.
export const getBillableRows = ({ projectId, contractorId, since, until, ...saved } = {}) => {
  const q = new URLSearchParams();
  if (projectId) q.set("projectId", projectId);
  if (contractorId) q.set("contractorId", contractorId);
  if (since) q.set("since", since);
  if (until) q.set("until", until);
  appendSavedFilters(q, saved);
  const qs = q.toString();
  return get(`/billable-hours/rows${qs ? `?${qs}` : ""}`);
};

// THE FULL REPORT AS A FILE: format "pdf" or "xlsx"; prices false for the
// hours-only document, which the SERVER builds without rates or costs - this
// call cannot ask for money the gate does not allow, it only declines it.
// Same filters as the page. Resolves { lines, filename }.
export const exportBillableHours = async (format, { prices = true, projectId, contractorId, since, until, ...saved } = {}) => {
  const q = new URLSearchParams({ format });
  if (!prices) q.set("prices", "0");
  if (projectId) q.set("projectId", projectId);
  if (contractorId) q.set("contractorId", contractorId);
  if (since) q.set("since", since);
  if (until) q.set("until", until);
  appendSavedFilters(q, saved);
  const token = localStorage.getItem(tokenRef);
  const response = await fetch(`${url}/billable-hours/export?${q}`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    let msg = `HTTP ${response.status}`;
    try { msg = (await response.json()).err || msg; } catch (e) { /* not json */ }
    throw new Error(msg);
  }
  const blob = await response.blob();
  const m = (response.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/);
  const filename = m ? m[1] : `contractor-hours.${format}`;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return { lines: Number(response.headers.get("X-Line-Count") || 0), filename };
};

// The overrides themselves. `appliesTo` is 'owner' (your reports only) or
// 'everyone' (their reports and digests too) - see the page for why that
// choice is a radio and never a default.
export const getOverrides = () => get("/billable-hours/overrides");

export const setOverride = (body) => globalPut("/billable-hours/overrides", body);

// remove() in api/index.js does NOT throw on a failed response - it returns
// the parsed body whatever the status. So a 404 would arrive here looking
// exactly like a success and the page would say "removed" about an override
// that is still in force. Turned into a rejection, because a delete that
// silently did nothing is the worst of the three outcomes.
export const removeOverride = (id) =>
  remove(`/billable-hours/overrides/${id}`, {}).then((body) => {
    if (body && body.err) throw body;
    if (!body || body.ok !== true) {
      const e = new Error("The override was not removed - reload and check.");
      e.err = e.message;
      throw e;
    }
    return body;
  });

// ONE ITEM'S RATE (an issue or a shop drawing, for one contractor). Checked
// before the contractor's project and account rates; saving again edits the
// same one. `system` is "issues" | "shopDrawings", `ref` the item id.
export const getItemOverrides = (projectId) =>
  get(`/billable-hours/item-overrides${projectId ? `?projectId=${projectId}` : ""}`);

export const setItemOverride = (body) => globalPut("/billable-hours/item-overrides", body);

// ONE RATE FOR MANY LINES: { items: [{ system, ref, contractorCompanyId,
// contractorName }], rate, appliesTo }. All or nothing - the server checks
// every line before writing any. Resolves { saved, issues, shopDrawings }.
export const bulkSetItemOverrides = (body) => globalPut("/billable-hours/item-overrides/bulk", body);

// LEAVE LINES OUT OF THE REPORT: { items: [{ system, ref, contractorCompanyId,
// contractorName }], reason }. All or nothing, like the group rate. Resolves
// { ignored, alreadyIgnored }. The report lists them under `ignored`.
export const bulkIgnoreItems = (body) => globalPut("/billable-hours/ignored/bulk", body);

// Bring ignored lines back: the `ignoredId`s from the report. Resolves { restored }.
export const restoreIgnoredItems = (ids) => globalPut("/billable-hours/ignored/restore", { ids });

// Same reason as removeOverride: remove() resolves on a failed response.
export const removeItemOverride = (id) =>
  remove(`/billable-hours/item-overrides/${id}`, {}).then((body) => {
    if (body && body.err) throw body;
    if (!body || body.ok !== true) {
      const e = new Error("The item rate was not removed - reload and check.");
      e.err = e.message;
      throw e;
    }
    return body;
  });

// NO COST (owner, 2026-10-12): hours stay, the rate is hidden, the cost is
// $0.00. Not the same as ignoring a line, which takes it out of the report.
//
// Lines - one from the per-line box, many from the bulk action: { items:
// [{ system, ref, contractorCompanyId, contractorName }], noCost: true|false }.
// All or nothing, like the bulk rate. Resolves { changed, unchanged }.
export const setItemsNoCost = (items, noCost, reason = "") =>
  globalPut("/billable-hours/no-cost/items", { items, noCost, reason });

// A whole contractor on the given projects: { contractorCompanyId,
// contractorName, projectIds, noCost }. Resolves { changed, projects }.
export const setContractorNoCost = (body) => globalPut("/billable-hours/no-cost/contractor", body);

// SEND THE REPORT TO A RECIPIENT LIST (owner, 2026-10-21). `query` is the
// page's filters as one object (the same keys the report's query string
// takes; projectId required), `items` the ticked lines' itemKeys or null for
// everything shown. The preview answers who would receive which copy and the
// mail's summary; the send takes { recipients: { team: [emails],
// contractors: { [companyId]: [emails] } }, message }.
export const reportQuery = ({ projectId, contractorId, since, until, ...saved } = {}) => {
  const q = new URLSearchParams();
  if (projectId) q.set("projectId", projectId);
  if (contractorId) q.set("contractorId", contractorId);
  if (since) q.set("since", since);
  if (until) q.set("until", until);
  appendSavedFilters(q, saved);
  return Object.fromEntries(q.entries());
};
export const previewReportSend = (body) => post("/billable-hours/send/preview", body);
export const sendReport = (body) => post("/billable-hours/send", body);

// ---- the report log (owner, 2026-10-22) ---------------------------------
//
// WHAT WOULD BE ON THIS REPORT THAT HAS ALREADY BEEN REPORTED. Takes the SAME
// filters as the export, because the answer has to be about the document the
// user is about to produce - a saved filter or the reader's project scope
// changes which items are on it, and an alert about items that would not have
// been included is worse than no alert at all.
// Resolves { items, already, issues, shopDrawings, rows: [{ system, ref,
// code, label, at, by, periodLabel, times, hours, cost }] }.
export const precheckBillableExport = ({ prices = true, projectId, contractorId, since, until, ...saved } = {}) => {
  const q = new URLSearchParams();
  if (!prices) q.set("prices", "0");
  if (projectId) q.set("projectId", projectId);
  if (contractorId) q.set("contractorId", contractorId);
  if (since) q.set("since", since);
  if (until) q.set("until", until);
  appendSavedFilters(q, saved);
  const qs = q.toString();
  return get(`/billable-hours/export/precheck${qs ? `?${qs}` : ""}`);
};

// PREVIOUS EXPORTED REPORTS, newest first. Headers only - the items are a
// second call, so a tab showing twenty reports does not load twenty reports'
// worth of items. `cost` is null, not 0, on an hours-only report.
export const listBillableExports = ({ projectId, limit } = {}) => {
  const q = new URLSearchParams();
  if (projectId) q.set("projectId", projectId);
  if (limit) q.set("limit", String(limit));
  const qs = q.toString();
  return get(`/billable-hours/exports${qs ? `?${qs}` : ""}`);
};

// EVERY ITEM THAT WAS ON ONE REPORT - the owner's "detail option to list all
// items". Scoped to the caller's account on the server; another tenant's id
// is a 404, never a document the client is trusted not to show.
export const getBillableExport = (id) => get(`/billable-hours/exports/${id}`);
